import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { encodeData } from '../../../utils/encodeAndEncode';
import { getCourseApprovalANDApprovedANDRejected, approveCourse } from '../../../services/adminService';
import Loader from '../../../components/common/Loader';
import { usePermissions } from '../../../context/PermissionsContext.js';

const defaultCourseImage = '/images/admin/course/course1.png';

function Rejected({ searchTerm = '', setSearchTerm, registerRefresh }) {
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const [rejectedCoursesData, setRejectedCoursesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedCourseName, setSelectedCourseName] = useState('');
    const [processing, setProcessing] = useState(false);

    const getRejectedCourses = async () => {
        try {
            setLoading(true);
            const response = await getCourseApprovalANDApprovedANDRejected();
            const rejectedCourses = response.data.rejected || [];
            setRejectedCoursesData(rejectedCourses);
        } catch (error) {
            console.error('Error fetching rejected courses:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getRejectedCourses();
    }, []);

    useEffect(() => {
        if (registerRefresh) registerRefresh(getRejectedCourses);
    }, [registerRefresh]);

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const getStatusClass = (status) => {
        return 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 border border-red-200 text-red-700';
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    // Search functionality
    const filteredData = rejectedCoursesData.filter(item => {
        const searchString = searchTerm.toLowerCase();
        return (
            item.course_name?.toLowerCase().includes(searchString) ||
            item.trainer_name?.toLowerCase().includes(searchString) ||
            item.trainer_email?.toLowerCase().includes(searchString) ||
            item.course_desc?.toLowerCase().includes(searchString)
        );
    });

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    // Page navigation
    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    // Items per page change handler
    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    const openApproveModal = (courseId, courseName) => {
        if (!permissions.course_approval_workflow_view_analytics) {
            toast.warning("You don't have permission to reconsider courses");
            return;
        }
        setSelectedCourseId(courseId);
        setSelectedCourseName(courseName);
        setShowApproveModal(true);
    };

    const handleApprove = async () => {
        try {
            setProcessing(true);
            await approveCourse({ id: selectedCourseId });
            // Remove the approved course from the list
            setRejectedCoursesData(prev => prev.filter(course => course.id !== selectedCourseId));
            // Close modal and reset states
            setShowApproveModal(false);
            setSelectedCourseId(null);
            setSelectedCourseName('');
            // Show success message
            toast.success('Course approved successfully!');
        } catch (error) {
            console.error('Error approving course:', error);
            toast.error('Failed to approve course. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const handleViewCourse = (courseId, e) => {
        // Prevent row click when clicking on action buttons
        if (e && e.target.closest('.action-buttons')) {
            return;
        }
        const encodedCourseId = encodeData( courseId);
        navigate(`/admin/approvalRequest/view/${encodedCourseId}`);
    };

    return (
        <div className="approval-rejected-section" style={{ fontFamily: 'var(--font-primary)' }}>
            {/* Premium Table Container */}
            <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50 border-b border-gray-100">
                                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest w-[35%]">Course Details</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest w-40">Delisted Date</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest w-40">Instructor</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest w-[25%] px-4">Reason for Delisting</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-widest text-center w-36">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-24 text-center">
                                        <div className="flex flex-col items-center">
                                            <div className="w-10 h-10 border-4 border-[#7083FF]/20 border-t-[#7083FF] rounded-full animate-spin mb-4" />
                                            <p className="text-gray-400 font-bold text-xs uppercase tracking-widest">Scanning Catalog...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : currentItems.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-24 text-center">
                                        <div className="flex flex-col items-center max-w-sm mx-auto">
                                            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                                                <Icon icon="fluent:delete-dismiss-24-regular" className="text-red-400" width={40} />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                                {searchTerm ? 'No matches found' : 'No Delisted Courses'}
                                            </h3>
                                            <p className="text-gray-500 text-sm leading-relaxed">
                                                {searchTerm
                                                    ? `We couldn't find any delisted courses matching "${searchTerm}".`
                                                    : 'The delisted courses archive is currently empty.'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((course) => (
                                    <tr 
                                        key={course.id} 
                                        onClick={(e) => handleViewCourse(course.id, e)}
                                        className="hover:bg-gray-50/80 transition-all duration-300 group cursor-pointer"
                                    >
                                        <td className="px-6 py-6">
                                            <div className="flex gap-5">
                                                <div className="relative shrink-0">
                                                    <img
                                                        src={course.banner_image || defaultCourseImage}
                                                        alt={course.course_name}
                                                        className="w-44 h-28 rounded-2xl object-cover border border-gray-100 shadow-sm transition-transform duration-500 group-hover:scale-[1.02] grayscale-[30%] group-hover:grayscale-0"
                                                        onError={(e) => { e.target.src = defaultCourseImage; }}
                                                    />
                                                </div>
                                                <div className="min-w-0 flex flex-col justify-between py-0.5">
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 text-base leading-snug line-clamp-1 group-hover:text-red-600 transition-colors">
                                                            {course.course_name}
                                                        </h4>
                                                        <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2">
                                                            <div className="flex items-center gap-1.5 text-gray-400">
                                                                <Icon icon="fluent:video-24-regular" className="text-red-400" width={14} />
                                                                <span className="text-[11px] font-bold uppercase tracking-tight">{course.course_summary?.total_videos || 0} Videos</span>
                                                            </div>
                                                            <div className="flex items-center gap-1.5 text-gray-400 border-l border-gray-100 pl-4">
                                                                <Icon icon="fluent:clock-24-regular" className="text-red-400" width={14} />
                                                                <span className="text-[11px] font-bold uppercase tracking-tight">{course.course_summary?.total_duration?.split('.')[0] || '00:00:00'}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-auto">
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-widest border border-red-100/50">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                                            DELISTED
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 align-middle">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-gray-900">{formatDate(course.request_date)}</span>
                                                <span className="text-[11px] text-gray-400 font-medium mt-0.5">Modified</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 align-middle">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-red-400 font-bold text-xs uppercase">
                                                    {course.trainer_name?.charAt(0) || 'U'}
                                                </div>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm font-bold text-gray-900 truncate">{course.trainer_name}</span>
                                                    <span className="text-[11px] text-gray-400 font-medium truncate">Instructor</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 align-middle">
                                            <div className="max-w-[200px]">
                                                <p className="text-sm text-gray-500 italic leading-relaxed line-clamp-2 bg-gray-50/50 p-3 rounded-xl border border-gray-100">
                                                    &ldquo;{course.rejected_note || course.rejection_reason || 'No reason provided'}&rdquo;
                                                </p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 align-middle">
                                            <div className="flex items-center justify-center gap-2 action-buttons opacity-100 sm:opacity-0 group-hover:opacity-100 transition-all duration-300">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); openApproveModal(course.id, course.course_name); }}
                                                    disabled={processing || !permissions.course_approval_workflow_view_analytics}
                                                    className="p-3 rounded-xl bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all shadow-sm border border-primary/20 flex items-center justify-center"
                                                    title="Restore Course"
                                                >
                                                    <Icon icon="fluent:arrow-reset-24-regular" width={18} />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleViewCourse(course.id, e); }}
                                                    className="p-3 rounded-xl bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-all shadow-sm border border-gray-100 flex items-center justify-center"
                                                    title="Review Details"
                                                >
                                                    <Icon icon="fluent:eye-24-regular" width={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Container */}
            {!loading && filteredData.length > 0 && (
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-6 px-6 py-4 bg-white rounded-3xl border border-gray-100 shadow-sm">
                    <div className="text-sm text-gray-500 font-medium">
                        Showing <span className="font-black text-gray-900">{indexOfFirstItem + 1}</span>–<span className="font-black text-gray-900">{Math.min(indexOfLastItem, filteredData.length)}</span> of <span className="font-black text-gray-900">{filteredData.length}</span> entries
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Per Page</span>
                            <select
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                className="px-3 py-2 rounded-xl border border-gray-100 bg-gray-50/50 text-xs font-bold text-gray-900 focus:outline-none focus:ring-4 focus:ring-[#7083FF]/5 transition-all"
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-1.5 ml-2 border-l border-gray-100 pl-4">
                            <button
                                type="button"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-2.5 rounded-xl border border-gray-100 text-gray-600 hover:bg-gray-50 disabled:opacity-30 transition-all"
                            >
                                <Icon icon="fluent:chevron-left-24-regular" width={18} />
                            </button>
                            <div className="px-4 text-xs font-black text-gray-900 uppercase tracking-widest bg-gray-50 py-2.5 rounded-xl border border-gray-100">
                                {currentPage} <span className="text-gray-400 font-bold mx-1">/</span> {totalPages}
                            </div>
                            <button
                                type="button"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="p-2.5 rounded-xl border border-gray-100 text-gray-600 hover:bg-gray-50 disabled:opacity-30 transition-all"
                            >
                                <Icon icon="fluent:chevron-right-24-regular" width={18} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reconsider Modal Enhancement */}
            {showApproveModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-[70] p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-[32px] shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300 border border-white/20">
                        <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                                    <Icon icon="fluent:arrow-reset-24-regular" width={24} />
                                </div>
                                <h5 className="text-lg font-black text-gray-900 tracking-tight">Reconsider Course</h5>
                            </div>
                            <button
                                type="button"
                                className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                                onClick={() => { setShowApproveModal(false); setSelectedCourseId(null); setSelectedCourseName(''); }}
                            >
                                <Icon icon="fluent:dismiss-24-regular" width={24} />
                            </button>
                        </div>
                        <div className="p-8">
                            <p className="text-gray-600 leading-relaxed font-medium">
                                Are you sure you want to restore <span className="text-gray-900 font-black">&quot;{selectedCourseName}&quot;</span>? This will move it back to the approved catalalog and make it live.
                            </p>
                        </div>
                        <div className="px-8 py-6 bg-gray-50/50 flex flex-col sm:flex-row gap-3">
                            <button
                                type="button"
                                className="flex-1 px-6 py-3.5 rounded-2xl border border-gray-200 text-gray-700 font-bold hover:bg-white transition-all text-sm"
                                onClick={() => { setShowApproveModal(false); setSelectedCourseId(null); setSelectedCourseName(''); }}
                            >
                                No, Keep Archive
                            </button>
                            <button
                                type="button"
                                className="flex-1 px-6 py-3.5 rounded-2xl bg-primary hover:bg-primary/90 text-white font-bold transition-all shadow-lg shadow-primary/20 flex items-center justify-center gap-2 text-sm"
                                onClick={handleApprove}
                                disabled={processing}
                            >
                                {processing ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        <span>Restoring...</span>
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="fluent:checkmark-24-regular" width={20} />
                                        <span>Yes, Restore</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Rejected
